import * as React from 'react';
import { omit } from 'rambda';
import { WishlistHelper, Product, WishListItem } from '@packages/common';

import { SideEffectsContext } from '../SideEffectsContext';
import { WishlistContext } from './WishlistContext';

export interface WishlistHelperContextValue {
  wishlistHelper: WishlistHelper;
}

export const WishlistHelperContext = React.createContext({} as WishlistHelperContextValue);

export interface WishlistHelperProviderProps {}

export const WishlistHelperProvider: React.FC<WishlistHelperProviderProps> = ({ children }) => {
  const { wishlistSubject } = React.useContext(WishlistContext);
  const sideEffectDispatcher = React.useContext(SideEffectsContext);

  const update = (updatedItems: WishListItem[]) => {
    wishlistSubject.next(updatedItems);
  };

  const addToWishlist = (item: Product) => {
    const wishlistItems = wishlistSubject.getValue();
    const wishlistAlreadyContainsItem = wishlistItems.find((i) => i.objectID === item.objectID);
    if (!wishlistAlreadyContainsItem) {
      const wishlistItem: WishListItem = omit(['features'], item);
      const updatedItems = [...wishlistItems, wishlistItem];
      sideEffectDispatcher.addWishlist(item);
      update(updatedItems);
    }
  };

  const removeFromWishlist = (product: Product) => {
    const wishlistItems = wishlistSubject.getValue();
    const updatedItems = wishlistItems.filter((item) => product.objectID !== item.objectID);
    sideEffectDispatcher.removeFromWishlist(product);
    update(updatedItems);
  };

  const wishlistToggle = (item: Product, isWislisted: boolean) => {
    if (isWislisted) {
      removeFromWishlist(item);
    } else {
      addToWishlist(item);
    }
  };

  const isWishlisted = (product: Product) => {
    return wishlistSubject.getValue().reduce((acc, next) => {
      return acc || next.objectID === product.objectID;
    }, false);
  };

  const wishlistHelper = React.useRef<WishlistHelper>({
    toggle: wishlistToggle,
    subscribe: wishlistSubject.subscribe,
    getWishlistedItemsIds: () => wishlistSubject.getValue().map((item) => item.objectID),
    isWishlisted,
    getWishListItems: () => wishlistSubject.getValue(),
  });
  return (
    <WishlistHelperContext.Provider value={{ wishlistHelper: wishlistHelper.current }}>
      {children}
    </WishlistHelperContext.Provider>
  );
};
